import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import withAuth from '../../auth/WithAuth';
import './artisanupdates.scss';
import FurnitureNavbar from '../../../components/layout/Nav';
import { Badge, Modal, Button, Spinner } from 'react-bootstrap';

import {
  fetchArtisanAcceptedProjects,
  setSelectedProjectForDetails,
} from '../../../redux/slices/projectsSlice';
import { motion, AnimatePresence } from 'framer-motion';
import { FaComments, FaEnvelope } from 'react-icons/fa';
import { IoClose } from 'react-icons/io5';
import { useNavigate } from 'react-router-dom';

const MyProjects = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [activeImage, setActiveImageState] = useState(0);
  const [selectedImage, setSelectedImage] = useState(null);
  const { projects, selectedProjectForDetails, loading } = useSelector(
    (state) => state.projects
  );

  const [filter, setFilter] = useState('All');
  const [isMarkingRead, setIsMarkingRead] = useState(false);

  // Fetch projects on mount
  useEffect(() => {
    dispatch(fetchArtisanAcceptedProjects());
  }, [dispatch]);



  // Handle opening messages page
  const handleOpenMessages = async (project) => {
    // Mark messages as read before opening
   
    
    // Navigate to messages page
    navigate('/messaging');
  };

  const filteredProjects =
    projects?.filter((p) => filter === 'All' || p.status === filter) || [];

  return (
    <>
      <FurnitureNavbar />
      <div className="my-projects-page">
        <section className="hero-banner">
          <div className="hero-overlay">
            <div className="hero-content">
              <h1>🛠️ Make Project Updates</h1>
              <p>Communicate With Clients On Project Progress</p>
            </div>
          </div>
        </section>

        <div className="nav-pills">
          {['All', 'In Progress', 'Completed'].map((tab) => (
            <button
              key={tab}
              className={`nav-link ${filter === tab ? 'active' : ''}`}
              onClick={() => setFilter(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="projects-grid container">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading projects...</p>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <h3>No projects in this category</h3>
              <p>Switch tabs to see other projects.</p>
            </div>
          ) : (
            <AnimatePresence>
              {filteredProjects.map((project, index) => (
                <motion.div
                  key={project.id}
                  className="project-card"
                  initial={{ opacity: 0, y: 20, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -20, scale: 0.95 }}
                  transition={{
                    duration: 0.4,
                    delay: index * 0.1,
                    type: 'spring',
                    stiffness: 100,
                  }}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                >
                  {/* Notification badge */}
                  {project.unread_updates > 0 && (
                    <div className="notification-badge-left">
                      <Badge bg="danger" pill className="unread-badge-pulse">
                        <FaComments className="me-1" />
                        {project.unread_updates > 99 ? '99+' : project.unread_updates}
                      </Badge>
                    </div>
                  )}

                  <div className="card-image-container">
                    {project.images?.length > 0 ? (
                      <img
                        src={project.images[0]}
                        alt={project.title}
                        className="card-image"
                      />
                    ) : (
                      <div className="no-image-placeholder">
                        <div className="placeholder-icon">🛠️</div>
                        <span>No Image</span>
                      </div>
                    )}
                    <div className="card-overlay">
                      <div className="status-badge">
                        <span
                          className={`status-dot ${project.status?.toLowerCase()}`}
                        ></span>
                        {project.status || 'Unknown'}
                      </div>
                    </div>
                  </div>

                  <div className="card-content">
                    <h3 className="card-title">{project.title}</h3>

                    <div className="card-meta">
                      <div className="meta-item">
                        <span className="meta-label">Client</span>
                        <span className="meta-value">{project.client}</span>
                      </div>

                      <div className="meta-item">
                        <span className="meta-label">Deadline</span>
                        <span className="meta-value">
                          {project.deadline
                            ? new Date(project.deadline).toLocaleDateString()
                            : 'Not set'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="progress-section">
                    <div className="progress-header">
                      <span>Progress</span>
                      <span className="progress-percent">
                        {project.progress || 0}%
                      </span>
                    </div>
                    <div className="custom-progress">
                      <div
                        className="progress-bar"
                        style={{ width: `${project.progress || 0}%` }}
                      />
                    </div>
                  </div>

                  <div className="card-actions">
                    <button
                      className="btn view-btn"
                      onClick={() =>
                        dispatch(setSelectedProjectForDetails(project))
                      }
                    >
                      <i className="bi bi-eye me-1"></i> View Details
                    </button>
                    <button
                      className={`btn chat-btn ${
                        project.unread_updates > 0 ? 'has-unread' : ''
                      }`}
                      onClick={() => handleOpenMessages(project)}
                      disabled={isMarkingRead}
                    >
                      <FaEnvelope className="me-1" />
                      {project.unread_updates > 0 ? `Messages (${project.unread_updates})` : 'Messages'}
                    </button>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          )}
        </div>

        {/* Project Details Modal */}
        <Modal
          show={!!selectedProjectForDetails}
          onHide={() => dispatch(setSelectedProjectForDetails(null))}
          size="lg"
          centered
        >
          <Modal.Header closeButton>
            <div>
              <Modal.Title className="mb-2">
                {selectedProjectForDetails?.title}
              </Modal.Title>
              <div className="d-flex gap-2">
                <Badge
                  bg={
                    selectedProjectForDetails?.status === 'In Progress'
                      ? 'warning'
                      : selectedProjectForDetails?.status === 'Completed'
                      ? 'success'
                      : 'secondary'
                  }
                >
                  {selectedProjectForDetails?.status}
                </Badge>
              </div>
            </div>
          </Modal.Header>

          <Modal.Body>
            {selectedProjectForDetails && (
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <img
                      src={
                        (selectedProjectForDetails.images || [])[activeImage] ||
                        'https://via.placeholder.com/400x250?text=No+Image'
                      }
                      alt="Project"
                      className="img-fluid rounded"
                      style={{
                        height: '250px',
                        width: '100%',
                        objectFit: 'cover',
                      }}
                    />
                  </div>
                  {(selectedProjectForDetails.images || []).length > 1 && (
                    <div className="thumbnails-active-projects d-flex gap-2 justify-content-center">
                      {(selectedProjectForDetails.images || []).map(
                        (img, idx) => (
                          <img
                            key={idx}
                            src={img}
                            alt={`Thumb ${idx + 1}`}
                            className={`rounded cursor-pointer ${
                              activeImage === idx
                                ? 'border border-[#673200]'
                                : 'opacity-75'
                            }`}
                            style={{
                              width: '60px',
                              height: '60px',
                              objectFit: 'cover',
                            }}
                            onClick={() => setActiveImageState(idx)}
                          />
                        )
                      )}
                    </div>
                  )}
                </div>

                <div className="col-md-6 d-flex flex-column gap-3">
                  <div className="p-3 bg-light rounded shadow-sm">
                    <h6 className="text-muted mb-3">Project Information</h6>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Client</span>
                      <span className="fw-semibold">
                        {selectedProjectForDetails.client}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Budget</span>
                      <span className="fw-semibold">
                        KSh{' '}
                        {selectedProjectForDetails.amount?.toLocaleString() ||
                          '0'}
                      </span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="text-muted">Deadline</span>
                      <span className="fw-semibold text-danger">
                        {selectedProjectForDetails.deadline
                          ? new Date(
                              selectedProjectForDetails.deadline
                            ).toLocaleDateString()
                          : 'Not set'}
                      </span>
                    </div>
                  </div>

                  <div className="p-3 bg-light rounded shadow-sm">
                    <h6 className="text-muted mb-2">Description</h6>
                    <p className="mb-0">
                      {selectedProjectForDetails.description ||
                        'No description available'}
                    </p>
                  </div>

                  <div className="p-3 bg-light rounded shadow-sm">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="text-muted mb-0">Project Progress</h6>
                      <span className="fw-bold text-primary">
                        {selectedProjectForDetails.progress || 0}%
                      </span>
                    </div>
                    <div className="progress mb-3">
                      <div
                        className="progress-bar"
                        style={{
                          width: `${selectedProjectForDetails.progress || 0}%`,
                        }}
                      />
                    </div>
                  </div>

                  {/* Messages button in details modal */}
                  <div className="p-3 bg-light rounded shadow-sm">
                    <div className="d-flex justify-content-between align-items-center">
                      <h6 className="text-muted mb-0">Project Communication</h6>
                      <Button
                      className='project-communication'
                        variant="primary"
                        size="sm"
                        onClick={() => {
                          dispatch(setSelectedProjectForDetails(null));
                          handleOpenMessages(selectedProjectForDetails);
                        }}
                        disabled={isMarkingRead}
                      >
                        <FaEnvelope className="me-1" />
                        {selectedProjectForDetails.unread_updates > 0
                          ? `Messages (${selectedProjectForDetails.unread_updates})`
                          : 'Messages'}
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => dispatch(setSelectedProjectForDetails(null))}
            >
              Close
            </Button>
          </Modal.Footer>
        </Modal>

        {/* Image Viewer Modal */}
        <Modal
          show={!!selectedImage}
          onHide={() => setSelectedImage(null)}
          centered
          size="xl"
          className="image-viewer-modal"
          fullscreen="md-down"
        >
          <Modal.Body className="image-viewer-body p-0">
            {selectedImage && (
              <>
                <Button
                  variant="link"
                  className="viewer-close"
                  onClick={() => setSelectedImage(null)}
                >
                  <IoClose />
                </Button>
                <motion.img
                  key={selectedImage}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ duration: 0.3 }}
                  src={selectedImage}
                  alt="Selected update"
                  className="viewer-image"
                />
                <div className="image-info">
                  <span className="image-index">Project Update Image</span>
                  <span className="image-size">Click outside to close</span>
                </div>
              </>
            )}
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
};

export default withAuth(MyProjects, 'artisan');